import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  useTheme,
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

const IndiaTechieSignup: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    dateOfBirth: '',
    email: '',
    confirmEmail: '',
    phone: '',
    confirmPhone: '',
    address: '',
  });

  const steps = [
    'Country Selection',
    'Photo Capture',
    'Document Verification',
    'Personal Information',
    'Work Experience',
    'Education',
    'Review & Submit'
  ];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h4" sx={{ mb: 2 }}>🇮🇳</Typography>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              India Registration
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              You have selected India. Date format will be DD/MM/YYYY
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Required Documents:</strong><br />
                • Driving License, Aadhaar Card, or Indian Passport<br />
                • PAN Card (last 4 digits/letters visible)
              </Typography>
            </Alert>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CameraIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              Live Photo Capture
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Please follow the instructions to verify you are human
            </Typography>
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Instructions:</strong><br />
                • Look at the camera<br />
                • Turn your head right<br />
                • Turn your head left<br />
                • Look up<br />
                • Look down<br />
                • Smile naturally
              </Typography>
            </Alert>
            <Button
              variant="contained"
              size="large"
              startIcon={<CameraIcon />}
              sx={{
                background: 'linear-gradient(45deg, #FF6B35 30%, #F7931E 90%)',
                borderRadius: 2,
                px: 4,
                py: 1.5
              }}
            >
              Start Photo Capture
            </Button>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ py: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
              Document Verification
            </Typography>
            
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Step 1: ID Document
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Show your Driving License, Aadhaar Card, or Indian Passport to the camera
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<CameraIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  Capture ID Document
                </Button>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Step 2: PAN Card
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Show your PAN card with first 6 digits hidden and last 4 digits/letters clearly visible
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<CameraIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  Capture PAN Card
                </Button>
              </CardContent>
            </Card>

            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Auto-generated VerTechie ID:</strong> COM{formData.firstName.substring(0, 3).toUpperCase()}DDMMYYYY****
              </Typography>
            </Alert>
          </Box>
        );

      case 3:
        return (
          <Box sx={{ py: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
              Personal Information
            </Typography>
            
            <Box sx={{ display: 'grid', gap: 3 }}>
              <TextField
                label="First Name *"
                value={formData.firstName}
                onChange={handleInputChange('firstName')}
                fullWidth
                required
              />
              <TextField
                label="Middle Name (Optional)"
                value={formData.middleName}
                onChange={handleInputChange('middleName')}
                fullWidth
              />
              <TextField
                label="Last Name *"
                value={formData.lastName}
                onChange={handleInputChange('lastName')}
                fullWidth
                required
              />
              <TextField
                label="Date of Birth *"
                value={formData.dateOfBirth}
                onChange={handleInputChange('dateOfBirth')}
                placeholder="DD/MM/YYYY"
                fullWidth
                required
              />
              <TextField
                label="Email ID *"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                fullWidth
                required
              />
              <TextField
                label="Confirm Email ID *"
                type="email"
                value={formData.confirmEmail}
                onChange={handleInputChange('confirmEmail')}
                fullWidth
                required
              />
              <TextField
                label="Phone Number *"
                value={formData.phone}
                onChange={handleInputChange('phone')}
                fullWidth
                required
              />
              <TextField
                label="Confirm Phone Number *"
                value={formData.confirmPhone}
                onChange={handleInputChange('confirmPhone')}
                fullWidth
                required
              />
              <TextField
                label="Full Address *"
                multiline
                rows={3}
                value={formData.address}
                onChange={handleInputChange('address')}
                fullWidth
                required
              />
            </Box>
          </Box>
        );

      case 4:
        return (
          <Box sx={{ py: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
              Work Experience
            </Typography>
            
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Important:</strong> Please provide 100% accurate information. Any falsified or fake information will lead to disqualification and permanent blocking from our platform.
              </Typography>
            </Alert>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Add Work Experience
                </Typography>
                <Button
                  variant="contained"
                  sx={{
                    background: 'linear-gradient(45deg, #FF6B35 30%, #F7931E 90%)',
                    borderRadius: 2
                  }}
                >
                  Add Experience
                </Button>
              </CardContent>
            </Card>
          </Box>
        );

      case 5:
        return (
          <Box sx={{ py: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
              Education Details
            </Typography>
            
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Important:</strong> We verify all education details with official institutions. Any false information will result in permanent blocking.
              </Typography>
            </Alert>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Add Education
                </Typography>
                <Button
                  variant="contained"
                  sx={{
                    background: 'linear-gradient(45deg, #FF6B35 30%, #F7931E 90%)',
                    borderRadius: 2
                  }}
                >
                  Add Education
                </Button>
              </CardContent>
            </Card>
          </Box>
        );

      case 6:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              Review & Submit
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Please review your information and accept the terms and conditions
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                background: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)',
                borderRadius: 2,
                px: 4,
                py: 1.5
              }}
              onClick={() => navigate('/signup-success')}
            >
              Submit Registration Request
            </Button>
          </Box>
        );

      default:
        return 'Unknown step';
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      py: 4
    }}>
      <Container maxWidth="md">
        <Paper 
          elevation={24} 
          sx={{ 
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Box sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/techie-signup')}
                sx={{ mr: 2 }}
              >
                Back
              </Button>
              <Typography variant="h4" sx={{ fontWeight: 800, flexGrow: 1, textAlign: 'center' }}>
                India Techie Registration
              </Typography>
            </Box>

            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {renderStepContent(activeStep)}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                sx={{ borderRadius: 2 }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={activeStep === steps.length - 1}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  background: 'linear-gradient(45deg, #FF6B35 30%, #F7931E 90%)',
                  borderRadius: 2
                }}
              >
                {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default IndiaTechieSignup;





